/**
 * Register screen — email/password sign-up plus Sign up with Apple.
 *
 * PORTED from apps/cryb-native/app/(auth)/register.tsx (P3 AUTH group):
 *   - expo-router → react-navigation. A successful sign-up (either path) sets
 *     setOnboardingPending(true) + notifyAuthGate(); the useAuth() gate then
 *     swaps the root tree to Onboarding (replacing router.replace('/(auth)/onboarding')).
 *   - expo-apple-authentication + expo-crypto → services/apple-auth (invertase).
 *     The raw nonce returned by the lib is forwarded to the backend verbatim.
 *   - Ionicons → ui/Icon.
 *
 * P1-AUTH REDESIGN: gradient/blobs/plain <Text>CRYB</Text> → ParticleBackground +
 * GlassSurface + LogoWordmark, token-only styling. The old advisory strength meter
 * is replaced by a LIVE PASSWORD_RULES checklist (mirrors the server policy) with
 * per-requirement pass/fail. Server WEAK_PASSWORD field errors are surfaced inline;
 * a 429 shows the EXACT retry time from the server's `retryAfter`.
 */
import { useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
  Alert,
  Pressable,
  Linking,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '../components/ui/Icon';
import { isAppleAuthAvailable, isAppleAuthAvailableSync, performAppleLogin } from '../services/apple-auth';
import AppleSignInButton from '../components/AppleSignInButton';
import ParticleBackground from '../components/ui/ParticleBackground';
import LogoWordmark from '../components/LogoWordmark';
import { GlassSurface, GlassButton } from '../components/glass';
import { register, appleSignIn, setOnboardingPending, PASSWORD_RULES } from '../services/api';
import { notifyAuthGate } from '../navigation/useAuth';
import type { AuthStackParamList } from '../navigation/types';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../constants/theme';

type Nav = NativeStackNavigationProp<AuthStackParamList, 'Register'>;

// Format a seconds count as a short human string ("45 seconds" / "2 minutes").
function formatRetry(seconds: number): string {
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  const m = Math.ceil(seconds / 60);
  return `${m} minute${m === 1 ? '' : 's'}`;
}

export default function RegisterScreen() {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const cardWidth = layout !== 'phone'
    ? { maxWidth: layout === 'desktop' ? 460 : 420, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  // Explicit consent to Terms + Privacy gates account creation (Apple 1.2 / 5.1.1).
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Age gate — CRYB carries crypto features, so accounts require 18+.
  const [is18Plus, setIs18Plus] = useState(false);
  // Per-field errors surfaced from the server (username/email taken, weak password)
  // and a dedicated rate-limit banner for a 429 with an exact retry time.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [rateLimitMsg, setRateLimitMsg] = useState<string | null>(null);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [appleAvailable] = useState(() => isAppleAuthAvailableSync());

  // Live evaluation of each password requirement (mirrors the server policy).
  const ruleState = useMemo(
    () => PASSWORD_RULES.rules.map((r) => ({ ...r, ok: r.test(password) })),
    [password],
  );
  const allRulesPass = ruleState.every((r) => r.ok);

  // ── Handlers ──
  async function handleRegister() {
    setFieldErrors({});
    setRateLimitMsg(null);

    const nextErrors: Record<string, string> = {};
    if (!username.trim()) nextErrors.username = 'Choose a username.';
    if (!email.trim()) nextErrors.email = 'Enter your email.';
    if (!allRulesPass) nextErrors.password = 'Your password does not meet all requirements.';
    if (Object.keys(nextErrors).length > 0) {
      setPasswordTouched(true);
      setFieldErrors(nextErrors);
      return;
    }

    if (!is18Plus) {
      Alert.alert('Age requirement', 'You must be 18 or older to create an account.');
      return;
    }
    if (!agreedToTerms) {
      Alert.alert('Agreement required', 'Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }

    setLoading(true);
    const result = await register(username.trim(), email.trim(), password);
    setLoading(false);

    if (result.success) {
      // Email signup is always a new user → onboarding next. Set the pending flag
      // BEFORE notifying so the gate can't race register() (which flips authed) to home.
      setOnboardingPending(true);
      notifyAuthGate();
      return;
    }

    // ── Failure: surface EXACT, structured server feedback ──
    const details = (result as any).details as Record<string, any> | undefined;
    const code = (result as any).code as string | undefined;

    // Rate limited → show the exact wait the server dictates (retryAfter seconds).
    const retryAfter: number | undefined =
      typeof details?.retryAfter === 'number' ? details.retryAfter
      : typeof details?.retryAfterSeconds === 'number' ? details.retryAfterSeconds
      : undefined;
    if (code === 'RATE_LIMIT_EXCEEDED' || retryAfter != null) {
      setRateLimitMsg(
        retryAfter != null
          ? `Too many attempts. Try again in ${formatRetry(retryAfter)}.`
          : (result.error || 'Too many attempts. Please try again later.'),
      );
      return;
    }

    // Structured per-field errors (username/email taken, weak-password concerns).
    const next: Record<string, string> = {};
    const fe = details?.fieldErrors;
    if (fe && typeof fe === 'object') {
      // password field errors may be nested { minLength, uppercase, ... }.
      if (fe.password && typeof fe.password === 'object') {
        next.password = Object.values(fe.password as Record<string, string>).join('. ');
      } else if (typeof fe.password === 'string') {
        next.password = fe.password;
      }
      if (typeof fe.username === 'string') next.username = fe.username;
      if (typeof fe.email === 'string') next.email = fe.email;
    }
    // Fall back to keyword-matching the flat message when the server sent no map.
    if (Object.keys(next).length === 0) {
      const lower = (result.error || '').toLowerCase();
      if (/username/.test(lower)) next.username = result.error!;
      else if (/email/.test(lower) && /(registered|use|taken|exist)/.test(lower)) next.email = result.error!;
      else if (/password/.test(lower)) next.password = result.error!;
    }

    if (Object.keys(next).length > 0) {
      setPasswordTouched(true);
      setFieldErrors(next);
    } else {
      Alert.alert(
        'Registration failed',
        result.error || 'Something went wrong. Please try again or use a different email/username.',
      );
    }
  }

  // Sign up with Apple. Same token exchange as login, but new sign-ups continue
  // into onboarding. Availability is re-checked at tap time.
  async function handleAppleSignUp() {
    // Sign in with Apple is one-tap: no pre-checkboxes. Consent to Terms + Privacy
    // and the 18+ requirement is given implicitly by continuing (text shown beneath
    // the Apple button).
    try {
      const available = await isAppleAuthAvailable().catch(() => false);
      if (!available) {
        Alert.alert('Unavailable', 'Sign in with Apple is not available on this device.');
        return;
      }
      const credential = await performAppleLogin();
      if (!credential) return; // user canceled

      setLoading(true);
      const result = await appleSignIn(
        credential.identityToken,
        credential.fullName,
        credential.email,
        credential.rawNonce
      );
      setLoading(false);

      if (result.success) {
        if ((result as any).data?.isNewUser === false) {
          setOnboardingPending(false);
        } else {
          setOnboardingPending(true);
        }
        notifyAuthGate();
      } else {
        Alert.alert('Sign up failed', result.error || 'Could not sign up with Apple. Please try again.');
      }
    } catch (e: any) {
      setLoading(false);
      const detail = e?.code || e?.message ? ` (${e?.code || e?.message})` : '';
      Alert.alert('Sign up failed', `Could not complete Apple Sign In${detail}. Please try again.`);
    }
  }

  return (
    <View style={styles.root}>
      <ParticleBackground theme="light" count={7} />
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          style={styles.flex}
          contentContainerStyle={[
            styles.scrollContent,
            { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xxl },
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={[styles.stack, cardWidth]}>
            <View style={styles.brand}>
              <LogoWordmark size={46} style={styles.logoCentered} />
              <Text style={styles.tagline}>Create your account</Text>
            </View>

            <GlassSurface variant="card" radius={radius.xl} contentStyle={styles.cardBody} style={styles.card}>
              {/* Sign up with Apple */}
              {appleAvailable && (
                <>
                  <View style={styles.appleAuthButton}>
                    <AppleSignInButton
                      label="Sign up with Apple"
                      onPress={handleAppleSignUp}
                      disabled={loading}
                    />
                  </View>
                  <Text style={styles.appleConsent}>
                    By continuing you confirm you are 18+ and agree to our{' '}
                    <Text style={styles.termsLink} onPress={() => navigation.navigate('Legal', { doc: 'terms' })}>Terms</Text>
                    {' '}and{' '}
                    <Text style={styles.termsLink} onPress={() => navigation.navigate('Legal', { doc: 'privacy' })}>Privacy Policy</Text>.
                  </Text>
                  <View style={styles.dividerRow}>
                    <View style={styles.dividerLine} />
                    <Text style={styles.dividerText}>or</Text>
                    <View style={styles.dividerLine} />
                  </View>
                </>
              )}

              {rateLimitMsg && (
                <View style={styles.rateLimitBanner}>
                  <Ionicons name="time-outline" size={16} color={colors.warning} />
                  <Text style={styles.rateLimitText}>{rateLimitMsg}</Text>
                </View>
              )}

              {/* Username */}
              <TextInput
                style={[styles.input, fieldErrors.username && styles.inputError]}
                placeholder="Username"
                placeholderTextColor={colors.muted}
                value={username}
                onChangeText={(t) => { setUsername(t); if (fieldErrors.username) setFieldErrors((e) => ({ ...e, username: '' })); }}
                autoCapitalize="none"
                textContentType="username"
                autoComplete="username-new"
                returnKeyType="next"
              />
              {!!fieldErrors.username && <Text style={styles.fieldError}>{fieldErrors.username}</Text>}

              {/* Email */}
              <TextInput
                style={[styles.input, fieldErrors.email && styles.inputError]}
                placeholder="Email"
                placeholderTextColor={colors.muted}
                value={email}
                onChangeText={(t) => { setEmail(t); if (fieldErrors.email) setFieldErrors((e) => ({ ...e, email: '' })); }}
                autoCapitalize="none"
                keyboardType="email-address"
                textContentType="emailAddress"
                autoComplete="email"
                returnKeyType="next"
              />
              {!!fieldErrors.email && <Text style={styles.fieldError}>{fieldErrors.email}</Text>}

              {/* Password */}
              <View style={styles.passwordWrap}>
                <TextInput
                  style={[styles.passwordInput, fieldErrors.password && styles.inputError]}
                  placeholder="Password"
                  placeholderTextColor={colors.muted}
                  value={password}
                  onChangeText={(t) => { setPassword(t); setPasswordTouched(true); if (fieldErrors.password) setFieldErrors((e) => ({ ...e, password: '' })); }}
                  onFocus={() => setPasswordTouched(true)}
                  secureTextEntry={!showPass}
                  textContentType="newPassword"
                  autoComplete="password-new"
                  returnKeyType="done"
                  onSubmitEditing={handleRegister}
                />
                <Pressable
                  onPress={() => setShowPass(p => !p)}
                  style={styles.eyeBtn}
                  hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                  accessibilityRole="button"
                  accessibilityLabel={showPass ? 'Hide password' : 'Show password'}
                >
                  <Ionicons name={showPass ? 'eye-off-outline' : 'eye-outline'} size={20} color={colors.muted} />
                </Pressable>
              </View>
              {!!fieldErrors.password && <Text style={styles.fieldError}>{fieldErrors.password}</Text>}

              {/* Live PASSWORD_RULES checklist — shown once the field is touched. */}
              {passwordTouched && (
                <View style={styles.rulesList}>
                  {ruleState.map((r) => (
                    <View key={r.key} style={styles.ruleRow}>
                      <Ionicons
                        name={r.ok ? 'checkmark-circle' : 'ellipse-outline'}
                        size={15}
                        color={r.ok ? colors.success : colors.faint}
                      />
                      <Text style={[styles.ruleText, r.ok && styles.ruleTextOk]}>{r.label}</Text>
                    </View>
                  ))}
                </View>
              )}

              {/* Age gate (18+) */}
              <Pressable
                style={styles.termsRow}
                onPress={() => setIs18Plus((v) => !v)}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: is18Plus }}
                accessibilityLabel="I confirm I am 18 years or older"
                hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
              >
                <View style={[styles.checkbox, is18Plus && styles.checkboxChecked]}>
                  {is18Plus && <Ionicons name="checkmark" size={15} color={colors.white} />}
                </View>
                <Text style={styles.termsText}>I confirm I am 18 years of age or older.</Text>
              </Pressable>

              {/* Terms acceptance */}
              <Pressable
                style={styles.termsRow}
                onPress={() => setAgreedToTerms((v) => !v)}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: agreedToTerms }}
                accessibilityLabel="I agree to the Terms of Service and Privacy Policy"
                hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
              >
                <View style={[styles.checkbox, agreedToTerms && styles.checkboxChecked]}>
                  {agreedToTerms && <Ionicons name="checkmark" size={15} color={colors.white} />}
                </View>
                <Text style={styles.termsText}>
                  I agree to the{' '}
                  <Text style={styles.termsLink} onPress={() => navigation.navigate('Legal', { doc: 'terms' })}>
                    Terms of Service
                  </Text>{' '}
                  and{' '}
                  <Text style={styles.termsLink} onPress={() => navigation.navigate('Legal', { doc: 'privacy' })}>
                    Privacy Policy
                  </Text>
                </Text>
              </Pressable>

              {/* Create Account — disabled until terms + age accepted */}
              <GlassButton
                title="Create Account"
                onPress={handleRegister}
                loading={loading}
                disabled={loading || !agreedToTerms || !is18Plus}
                fullWidth
                size="lg"
                style={styles.submit}
              />
            </GlassSurface>

            {/* Login link */}
            <TouchableOpacity
              style={styles.bottomRow}
              onPress={() => navigation.navigate('Login')}
              activeOpacity={0.6}
              accessibilityRole="button"
              accessibilityLabel="Go to sign in"
            >
              <Text style={styles.bottomText}>
                Already have an account?{' '}
                <Text style={styles.bottomLink}>Sign in</Text>
              </Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.bg },
  flex: { flex: 1 },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.xxl,
  },
  stack: { width: '100%' },
  brand: {
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  logoGlass: {
    alignSelf: 'center',
  },
  logoGlassInner: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoCentered: {
    alignSelf: 'center',
  },
  tagline: {
    ...typographyScale.body,
    color: colors.ink2,
    marginTop: spacing.sm,
  },
  card: { marginBottom: spacing.xl },
  cardBody: { padding: spacing.xxl },
  passwordWrap: {
    position: 'relative',
  },
  passwordInput: {
    height: 48,
    backgroundColor: colors.white,
    borderRadius: radius.md,
    paddingHorizontal: spacing.lg,
    paddingRight: 44,
    ...typographyScale.body,
    color: colors.ink,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  },
  eyeBtn: {
    position: 'absolute',
    right: spacing.md,
    top: 14,
  },
  appleAuthButton: {
    height: 48,
    width: '100%',
    marginBottom: spacing.md,
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.cardBorder,
  },
  dividerText: {
    marginHorizontal: spacing.md,
    ...typographyScale.caption,
    color: colors.muted,
  },
  input: {
    height: 48,
    backgroundColor: colors.white,
    borderRadius: radius.md,
    paddingHorizontal: spacing.lg,
    ...typographyScale.body,
    color: colors.ink,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    marginBottom: spacing.md,
  },
  inputError: {
    borderColor: colors.error,
  },
  fieldError: {
    ...typographyScale.caption,
    color: colors.error,
    marginTop: -spacing.sm,
    marginBottom: spacing.md,
    marginLeft: spacing.xs,
  },
  rulesList: {
    marginTop: spacing.sm,
    marginBottom: spacing.lg,
    gap: spacing.xs,
  },
  ruleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  ruleText: {
    ...typographyScale.caption,
    color: colors.muted,
  },
  ruleTextOk: {
    color: colors.ink2,
    ...fonts.medium,
  },
  rateLimitBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.white,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.warning,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.lg,
  },
  rateLimitText: {
    flex: 1,
    ...typographyScale.caption,
    color: colors.ink2,
    ...fonts.medium,
  },
  submit: {
    marginTop: spacing.sm,
  },
  bottomRow: {
    alignSelf: 'center',
  },
  bottomText: {
    ...typographyScale.body,
    color: colors.ink2,
  },
  bottomLink: {
    color: colors.primary,
    ...fonts.semibold,
  },
  termsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginBottom: spacing.lg,
    paddingHorizontal: spacing.xs,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: radius.sm,
    borderWidth: 1.5,
    borderColor: colors.cardBorder,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  termsText: {
    flex: 1,
    ...typographyScale.caption,
    color: colors.muted,
    lineHeight: 18,
  },
  termsLink: {
    color: colors.primary,
    ...fonts.semibold,
  },
  appleConsent: {
    ...typographyScale.caption,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.xs,
  },
});
